import React, { useEffect, useMemo } from 'react';
import { Clock, Menu } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import TimeRangePicker, { parseCustomRange } from './TimeRangePicker';
import config from '../config';

/**
 * Granularity options, each with an approximate duration in minutes.
 * Used to filter out intervals that don't make sense for a given time range
 * (e.g. "1 minute" buckets across a 90-day window).
 */
const GRANULARITIES = [
  { value: '1m', label: '1 min',  minutes: 1 },
  { value: '5m', label: '5 min',  minutes: 5 },
  { value: '15m', label: '15 min', minutes: 15 },
  { value: '1h', label: '1 hour',  minutes: 60 },
  { value: '6h', label: '6 hours', minutes: 360 },
  { value: '1d', label: '1 day',   minutes: 1440 },
];

// Approx duration of each time-range token in minutes.
const RANGE_MINUTES = {
  '5m': 5, '15m': 15, '1h': 60, '6h': 360,
  '24h': 1440, '7d': 10080, '30d': 43200, '90d': 129600,
};

/** Resolve a (preset or custom) time-range token to its duration in minutes. */
function rangeMinutesFor(timeRange) {
  if (RANGE_MINUTES[timeRange]) return RANGE_MINUTES[timeRange];
  const c = parseCustomRange(timeRange);
  if (c) return Math.max(1, Math.round((c.to - c.from) / 60000));
  return 1440;
}

// Max number of buckets we want to render in any chart.
const MAX_BUCKETS = 500;

export default function TopBar({
  timeRange,
  onTimeRangeChange,
  granularity,
  onGranularityChange,
  onMobileMenu,
}) {
  const { user, logout } = useAuth();
  const location = useLocation();
  // Pages that have no time-series data don't need the Range/Interval picker.
  // The Kill Switch console shows LIVE state read straight from APIM named
  // values — it has no historical dimension at all, so a time range would be
  // actively misleading (an operator could believe they were looking at a
  // past posture rather than the current one).
  const hideTimeControls =
    location.pathname.startsWith('/model-testing') ||
    location.pathname.startsWith('/settings') ||
    location.pathname.startsWith('/traffic-controls') ||
    // Onboarding is a form/workflow page — a time range is meaningless.
    location.pathname.startsWith('/onboarding') ||
    // Real-Time shows a live rolling feed — a historical range is meaningless.
    location.pathname.startsWith('/realtime');

  // The Interval (granularity) picker only makes sense on pages that render a
  // time-series chart whose KQL buckets by bin(TimeGenerated, <interval>).
  // On table-only pages (Products list, Cost Center, User Activity,
  // Conversations) changing it does nothing, so we hide it to avoid a control
  // that appears functional but has no effect. Range stays visible on those
  // pages because every query still honors the time window.
  //   Interval-aware pages:
  //     • '/'              Dashboard        (getDashboardTimeSeries/token)
  //     • '/apis'          API Analytics    (getApiTimeSeries drill-down)
  //     • '/products/:id'  Product Detail   (getProductAnalytics)
  //   The Products LIST ('/products' exactly) is table-only, so we require a
  //   sub-path segment to enable Interval under /products.
  const showInterval =
    location.pathname === '/' ||
    location.pathname.startsWith('/apis') ||
    /^\/products\/[^/]+/.test(location.pathname);

  // Pages whose data comes from ApiManagementGatewayLogs (retained ~45 days)
  // must cap the time range to that retention window. Costs (Cost Management,
  // up to 1yr) and App Insights pages keep the full range.
  const gatewayRetentionDays = useMemo(() => {
    const capped =
      location.pathname === '/' ||
      location.pathname.startsWith('/apis') ||
      location.pathname.startsWith('/conversations');
    return capped ? config.gatewayRetentionDays : undefined;
  }, [location.pathname]);

  // If the active range exceeds the retention window (e.g. user picked 90d on
  // an unconstrained page then navigated to the Dashboard), coerce it back to
  // a safe default so charts don't query empty buckets.
  useEffect(() => {
    if (!gatewayRetentionDays) return;
    const capMs = gatewayRetentionDays * 24 * 60 * 60 * 1000;
    const currentMs = rangeMinutesFor(timeRange) * 60000;
    if (currentMs > capMs) {
      onTimeRangeChange('30d');
    }
  }, [gatewayRetentionDays, timeRange, onTimeRangeChange]);

  // Only show granularities that produce a reasonable number of buckets
  // for the selected time range.
  const validGranularities = useMemo(() => {
    const rangeMin = rangeMinutesFor(timeRange);
    return GRANULARITIES.filter((g) => {
      const buckets = rangeMin / g.minutes;
      return buckets >= 2 && buckets <= MAX_BUCKETS;
    });
  }, [timeRange]);

  // If the current granularity is invalid for the new time range, coerce it
  // to the closest sensible default (target ~60 buckets).
  useEffect(() => {
    if (validGranularities.length === 0) return;
    if (validGranularities.some((g) => g.value === granularity)) return;
    const rangeMin = rangeMinutesFor(timeRange);
    const target = validGranularities.reduce((best, g) => {
      const diff = Math.abs(rangeMin / g.minutes - 60);
      return diff < best.diff ? { g, diff } : best;
    }, { g: validGranularities[0], diff: Infinity }).g;
    onGranularityChange(target.value);
  }, [timeRange, granularity, validGranularities, onGranularityChange]);

  return (
    <header className="flex items-center justify-between gap-2 h-14 px-3 sm:px-6 bg-white text-sce-dark border-t-2 border-t-sce-green border-b border-gray-200 shadow-sm no-print">
      {/* Hamburger — opens the sidebar drawer on mobile only. */}
      <button
        type="button"
        onClick={onMobileMenu}
        className="lg:hidden p-2 -ml-1 text-gray-600 hover:text-sce-dark rounded-md hover:bg-gray-100"
        aria-label="Open menu"
      >
        <Menu className="w-5 h-5" />
      </button>

      {/* Time controls — clearly labeled to distinguish range vs. interval.
          Hidden on pages (e.g. Model Testing) that have no time-series data. */}
      {!hideTimeControls ? (
      <div className="flex items-center gap-2 sm:gap-4 min-w-0 flex-1 overflow-x-auto no-scrollbar">
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-xs font-medium text-gray-500 hidden md:inline">
            Range
          </span>
          <TimeRangePicker value={timeRange} onChange={onTimeRangeChange} maxDays={gatewayRetentionDays} />
        </div>

        {showInterval && (
        <>
        <div className="h-5 w-px bg-gray-200 hidden md:block shrink-0" aria-hidden="true" />

        <div className="flex items-center gap-2 shrink-0">
          <span className="text-xs font-medium text-gray-500 hidden md:inline">
            Interval
          </span>
          <div className="relative">
            <Clock className="w-3.5 h-3.5 text-gray-400 absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none" />
            <select
              value={granularity}
              onChange={(e) => onGranularityChange(e.target.value)}
              className="text-xs border border-gray-300 rounded-md pl-7 pr-7 py-1.5 bg-white text-gray-700 appearance-none cursor-pointer hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-sce-green/40"
              aria-label="Bucket interval"
              title="Bucket interval — how data is grouped within the selected range"
            >
              {validGranularities.map((g) => (
                <option key={g.value} value={g.value}>{g.label}</option>
              ))}
            </select>
            <svg
              className="w-3 h-3 text-gray-400 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none"
              viewBox="0 0 12 12" fill="none" aria-hidden="true"
            >
              <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
        </div>
        </>
        )}
      </div>
      ) : (
        <div className="min-w-0" />
      )}

      {/* Right Actions */}
      <div className="flex items-center gap-3">
        {user && (() => {
          // MSAL AccountInfo uses `username` (UPN/email) and `name`. Some
          // tenants leave `name` empty, in which case fall back to the
          // idTokenClaims or the local-part of the username.
          const claims = user.idTokenClaims || {};
          const displayName =
            user.name ||
            claims.name ||
            claims.preferred_username ||
            user.username ||
            user.email ||
            '';
          const displayEmail =
            user.username ||
            user.email ||
            claims.preferred_username ||
            claims.email ||
            '';
          const primary = displayName || displayEmail || 'User';
          const initial = (primary || '?')[0].toUpperCase();
          return (
            <div className="flex items-center gap-2 pl-3 border-l border-gray-200">
              <div
                className="w-7 h-7 rounded-full bg-sce-green flex items-center justify-center text-xs font-bold text-white"
                title={displayEmail || primary}
              >
                {initial}
              </div>
              <span
                className="text-sm text-sce-dark hidden sm:block"
                title={displayEmail || primary}
              >
                {primary}
              </span>
              <button
                onClick={logout}
                className="text-xs text-gray-500 hover:text-red-500 transition ml-1"
              >
                Sign out
              </button>
            </div>
          );
        })()}
      </div>
    </header>
  );
}
